/**
 * cubusRecordApi — standalone CRUD module for Cubus records.
 *
 * No Vue dependency, no component instance needed.
 * Uses useJwt directly for HTTP calls.
 *
 * Usage:
 *   import recordApi from '@/cubus/services/cubusRecordApi'
 *   const records = await recordApi.query('mountstep', { filter })
 *   const tpl = await recordApi.newRecord('mountstep', { filter })
 *   await recordApi.save('mountstep', record)
 *   await recordApi.delete('mountstep', record)
 */
import useJwt from '@/cubus/jwt/useJwt'

function getToken() {
  return useJwt.getCubusToken()
}

function apiCall(method, param) {
  return useJwt.query({
    token: getToken(),
    query: { method, mode: 'sync', param },
  })
}

// ── Thread polling (for async operations) ──
async function pollThread(threadName, maxAttempts = 60, intervalMs = 500) {
  for (let i = 0; i < maxAttempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const resp = await apiCall('thread', { threadname: threadName })
    const t = resp.data.thread || resp.data.result || resp.data
    if (t && t.status === 'done') return t.result || t
    if (t && t.status === 'error') throw new Error(t.error || 'Thread error')
    // eslint-disable-next-line no-await-in-loop
    const delay = Math.min(intervalMs * Math.pow(1.3, Math.min(i, 10)), 5000)
    await new Promise(r => setTimeout(r, delay))
  }
  throw new Error(`Thread ${threadName} timed out`)
}

// ── Resolve response (handle thread-based or direct) ──
async function resolveResponse(resp, resultKey) {
  if (resp.data.error) throw new Error(typeof resp.data.error === 'string' ? resp.data.error : JSON.stringify(resp.data.error))
  if (resp.data.thread) {
    const result = await pollThread(resp.data.thread)
    return resultKey ? (result[resultKey] || result) : result
  }
  if (resp.data.result) return resultKey ? (resp.data.result[resultKey] || resp.data.result) : resp.data.result
  return resp.data
}

const recordApi = {
  /**
   * Query records from an object.
   * @param {string} objectsid - Object SID (e.g. 'mountstep', 'cbsattributeparam')
   * @param {Object} options - { filter, limit, offset, objectid, orderby, groupby }
   * @returns {Promise<Object[]>} Array of record objects (each record has cells with {value, status} fields)
   */
  async query(objectsid, options = {}) {
    const param = {
      objectsid: options.objectid != null ? undefined : objectsid,
      objectid: options.objectid != null ? options.objectid : undefined,
      limit: options.limit || 200,
      offset: options.offset || 0,
      filter: options.filter || undefined,
      orderby: options.orderby || undefined,
      groupby: options.groupby || undefined,
    }
    const resp = await apiCall('query', param)
    const result = await resolveResponse(resp, 'query')
    const rs = result.recordset || result
    return rs.records || []
  },

  /**
   * Load a new record template.
   * @param {string} objectsid - Object SID
   * @param {Object} options - { filter, objectid }
   * @returns {Promise<Object>} Record template with empty cells
   */
  async newRecord(objectsid, options = {}) {
    const param = {
      objectsid: options.objectid != null ? undefined : objectsid,
      objectid: options.objectid != null ? options.objectid : undefined,
      filter: options.filter || undefined,
    }
    const resp = await apiCall('newrecord', param)
    const result = await resolveResponse(resp)
    const tpl = result.record || result
    if (!tpl || !tpl.record) throw new Error(`newrecord ${objectsid}: empty template`)
    return tpl.record
  },

  /**
   * Save (create or update) a record.
   * @param {string} objectsid - Object SID
   * @param {Object} record - The record data (cells with {value, status} fields)
   * @param {Object} options - { objectid }
   * @returns {Promise<Object>} Saved record
   */
  async save(objectsid, record, options = {}) {
    const param = {
      objectsid: options.objectid != null ? undefined : objectsid,
      objectid: options.objectid != null ? options.objectid : undefined,
      record,
    }
    const resp = await apiCall('saverecord', param)
    return resolveResponse(resp, 'record')
  },

  /**
   * Soft-delete a record (sets del flag).
   * @param {string} objectsid - Object SID
   * @param {Object} record - The record with del.value = true
   * @param {Object} options - { objectid }
   * @returns {Promise<Object>}
   */
  async delete(objectsid, record, options = {}) {
    const param = {
      objectsid: options.objectid != null ? undefined : objectsid,
      objectid: options.objectid != null ? options.objectid : undefined,
      record,
    }
    const resp = await apiCall('deleterecord', param)
    return resolveResponse(resp, 'record')
  },

  /**
   * Load entity references (entref).
   * @param {string} entitysid - e.g. 'formula', 'function', 'scalar', 'modoper'
   * @returns {Promise<Object[]>} Array of {value, sid, title}
   */
  async loadRefs(entitysid) {
    const resp = await apiCall('entref', { entityid: 0, entitysid })
    if (resp.data.thread) {
      const result = await pollThread(resp.data.thread)
      return result.ref || result.items || result.records || result || []
    }
    const r = resp.data.result || resp.data
    return r.ref || r.items || r.records || r || []
  },

  /**
   * Load ref details (formula parts, function args).
   * @param {string} refType - 'formula' or 'function'
   * @param {number[]} refIds - Array of ref IDs
   * @returns {Promise<Object[]>}
   */
  async loadRefDetails(refType, refIds) {
    const paramKey = refType === 'formula' ? 'formulaids' : 'functionids'
    const resp = await apiCall('refdetails', { [paramKey]: refIds.map(Number) })
    const result = resp.data.result || resp.data
    return result ? (result.functions || result.formulas || []) : []
  },

  // ── Convenience: find record by ID ──
  async findById(objectsid, id) {
    const records = await this.query(objectsid, {
      limit: 1,
      filter: {
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes: [],
          conds: [{
            isactive: true,
            oper: { sid: 'equal' },
            args: [
              { type: { sid: 'entitysid' }, value: 'id' },
              { type: { sid: 'value' }, value: String(id) },
            ],
          }],
        },
      },
    })
    if (records.length === 0) throw new Error(`${objectsid} id=${id} not found`)
    return records[0].cells || records[0]
  },

  // ── Convenience: soft-delete by ID ──
  async deleteById(objectsid, id) {
    const rec = await this.findById(objectsid, id)
    if (rec.del) { rec.del.value = true; rec.del.status = 'changed' }
    rec.status = 'changed'
    return this.delete(objectsid, rec)
  },

  // ── Filter builder helpers ──
  filter: {
    and(...conds) {
      return {
        isActive: true,
        node: {
          isactive: true,
          isexpanded: true,
          oper: { id: 1, sid: 'and', name: 'And' },
          nodes: [],
          conds,
        },
      }
    },
    or(...conds) {
      return {
        isActive: true,
        node: {
          isactive: true,
          isexpanded: true,
          oper: { id: 2, sid: 'or', name: 'Or' },
          nodes: [],
          conds,
        },
      }
    },
    cond(entitySid, operSid, value) {
      const operMeta = {
        equal: { name: '=', args: 2 },
        notequal: { name: '<>', args: 2 },
        greater: { name: '>', args: 2 },
        less: { name: '<', args: 2 },
        greaterorequal: { name: '>=', args: 2 },
        lessorequal: { name: '<=', args: 2 },
        like: { name: 'Like', args: 2 },
        contain: { name: 'Contain', args: 2 },
        isnull: { name: 'IsNull', args: 1 },
        isnotnull: { name: 'IsNotNull', args: 1 },
        in: { name: 'In', args: 2 },
      }
      const meta = operMeta[operSid] || { name: operSid, args: 2 }
      const args = [{ type: { sid: 'entitysid' }, value: entitySid }]
      if (meta.args >= 2 && value !== undefined) {
        args.push({ type: { sid: 'value' }, value: String(value) })
      }
      return { isactive: true, oper: { sid: operSid, name: meta.name, args: meta.args }, args }
    },
    /** Entity-based condition (for jsonfilter with full entity metadata) */
    entityCond(entity, operSid, secondArg) {
      const operMeta = {
        equal: { name: '=', args: 2 },
        notequal: { name: '<>', args: 2 },
        greater: { name: '>', args: 2 },
        less: { name: '<', args: 2 },
        isnull: { name: 'IsNull', args: 1 },
        isnotnull: { name: 'IsNotNull', args: 1 },
        like: { name: 'Like', args: 2 },
        in: { name: 'In', args: 2 },
      }
      const meta = operMeta[operSid] || { name: operSid, args: 2 }
      const entityArg = {
        type: { id: 1, sid: 'entity', name: 'Entity' },
        datatype: null,
        entityid: entity.id || null,
        entitysid: entity.sid || null,
        parententityid: entity.parententityid || null,
        value: {
          value: entity.id,
          title: entity.name || '',
          entitysid: entity.sid || '',
          datatype: entity.datatype || null,
          parententityid: entity.parententityid || null,
        },
      }
      const args = [entityArg]
      if (meta.args >= 2 && secondArg) args.push(secondArg)
      return { isactive: true, oper: { sid: operSid, name: meta.name, args: meta.args }, args }
    },
    valueArg(entity, val) {
      return {
        type: { id: 2, sid: 'value', name: 'Value' },
        datatype: entity ? (entity.datatype || null) : null,
        entityid: entity ? entity.id : null,
        entitysid: entity ? (entity.sid || null) : null,
        parententityid: entity ? (entity.parententityid || null) : null,
        value: val != null ? String(val) : '',
      }
    },
    paramEntityArg(entity) {
      return {
        type: { id: 3, sid: 'paramentity', name: 'ParamEntity' },
        datatype: null,
        entityid: entity ? entity.id : null,
        entitysid: entity ? (entity.sid || null) : null,
        parententityid: null,
        value: entity ? {
          value: entity.id,
          title: entity.name || '',
          entitysid: entity.sid || '',
          datatype: entity.datatype || null,
          parententityid: entity.parententityid || null,
        } : null,
      }
    },
  },

  // ── Modification builder helpers ──
  mod: {
    /** Build a single modification item */
    item(destEntity, operSid, operMap, refOrValue) {
      const destentity = {
        value: destEntity.id || destEntity.value,
        sid: destEntity.sid || '',
        title: destEntity.name || destEntity.title || '',
      }
      const oper = operMap[operSid] || operMap.value || { sid: operSid, title: operSid, value: 0 }
      const m = {
        isactive: true,
        name: '',
        descr: '',
        destentity,
        oper,
        value: { value: { value: null } },
        entity: { id: null, sid: null, title: null },
        scalar: { id: null, sid: null, title: null },
        formula: { id: null, sid: null, title: null },
        function: { id: null, sid: null, title: null },
      }
      if (operSid === 'value' && refOrValue != null) {
        m.value = { value: { value: String(refOrValue) } }
      } else if (['entity', 'paramentity', 'sourceentity'].includes(operSid) && refOrValue) {
        m.entity = {
          id: refOrValue.id || refOrValue.value, sid: refOrValue.sid || '', title: refOrValue.name || refOrValue.title || '', value: String(refOrValue.id || refOrValue.value),
        }
      } else if (['formula', 'function', 'scalar'].includes(operSid) && refOrValue) {
        m[operSid] = {
          id: refOrValue.value || refOrValue.id, sid: refOrValue.sid || '', title: refOrValue.title || '', value: String(refOrValue.value || refOrValue.id),
        }
      }
      return m
    },
    /** Wrap items into a modification set */
    set(items) {
      return { isactive: true, list: Array.isArray(items) ? items : [items] }
    },
  },
}

export default recordApi
